import { Localized, useLocalization } from '@fluent/react'
import TableContainer from '@mui/material/TableContainer'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Fab from '@mui/material/Fab'
import AddIcon from '@mui/icons-material/Add'
import { Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { useEffect, useMemo, useState } from 'react'
import { Box, ButtonGroup, Chip, FormControlLabel, Switch, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { FlexDiv, formatDate, parseISODate } from '../../app/Utils';
import { selectSettings } from '../settings/settingsSlice'
import { DataGrid, GridColDef, GridToolbarContainer } from "@mui/x-data-grid"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { CompanyToRegister, loadCompaniesToRegister } from './companiesToRegisterApi'
import { unwrapResult } from '@reduxjs/toolkit'
import { ErrorContainer } from '../../app/ErrorContainer'
import { LoadingContainer } from '../../app/LoadingContainer'
import { QuickSearchToolbar } from '../../app/QuickSearchToolbar'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'

// --- Toolbar z jednym przełącznikiem ---
const CompaniesToolbar = (props: any) => {
    const { l10n } = useLocalization();
    const { onlyUnregistered, onToggleOnlyUnregistered, ...rest } = props as {
        onlyUnregistered: boolean;
        onToggleOnlyUnregistered: (val: boolean) => void;
    };

    return (
        <GridToolbarContainer>
            <QuickSearchToolbar {...rest} />
            <Box sx={{ ml: 1 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={onlyUnregistered}
                            onChange={(e) => onToggleOnlyUnregistered(e.target.checked)}
                            size="small"
                        />
                    }
                    label={l10n.getString("only-unregistered", null, "Tylko niezarejestrowane")}
                />
            </Box>
        </GridToolbarContainer>
    );
};

// Bezpieczny parser 'YYYY-MM-DD' -> Date (bez problemów strefowych)
const parseYMD = (s?: string | null) => {
    if (!s) return null as unknown as Date;
    const [y, m, d] = s.split('-').map(Number);
    if (!y || !m || !d) return null as unknown as Date;
    return new Date(y, m - 1, d);
};

export const CompaniesToRegister = () => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    const [companies, setCompanies] = useState<CompanyToRegister[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const dispatch = useAppDispatch()

    // --- Jeden przełącznik ---
    const [onlyUnregistered, setOnlyUnregistered] = useState<boolean>(false);

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadCompaniesToRegister())
            .then(unwrapResult)
            .then(setCompanies)
            .catch((error) => { console.error(error); setIsError(true); setError(error) })
            .finally(() => setIsLoading(false));
    }, [dispatch])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;
        return locale === 'pl'
            ? plPL.components.MuiDataGrid.defaultProps.localeText
            : defaultEnLocaleText;
    };

    // --- Filtrowanie jednym przełącznikiem ---
    const filteredCompanies = useMemo(() => {
        return onlyUnregistered
            ? companies.filter(c => !c.companyId)
            : companies;
    }, [companies, onlyUnregistered]);

    const columns: GridColDef[] = [

        {
            field: 'companyName',
            headerName: l10n.getString("companies-short-name", null, "Nazwa skrócona"),
            flex: 1
        },
        {
            field: 'email',
            width: 400,
            headerName: l10n.getString("user", null, "Użytkownik"),
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            valueGetter: (_: any, row: any) => row.companyId
                ? l10n.getString("companies-registered", null, "Zarejestrowana")
                : l10n.getString("companies-unregistered", null, "Niezarejestrowana"),

            renderCell: (params) => {
                const isRegistered = !!params.row.companyId;
                const label = isRegistered
                    ? l10n.getString("companies-registered", null, "Zarejestrowana")
                    : l10n.getString("companies-unregistered", null, "Niezarejestrowana");

                return (
                    <Chip
                        icon={isRegistered ? <CheckCircleIcon /> : <HourglassBottomIcon />}
                        label={label}
                        size="small"
                        color={isRegistered ? 'success' : 'error'}
                        variant={isRegistered ? 'outlined' : 'filled'}
                        sx={{
                            fontWeight: 500,
                            '.MuiChip-icon': { fontSize: 18 }
                        }}
                    />
                );
            },
        },
        {
            field: 'createDate',
            headerName: l10n.getString("created-at", null, "Utworzono"),
            width: 140,
            renderCell: (params) => {
                const date = formatDate(parseISODate(params.value));
                return date ? date : '';
            }
        },
        {
            field: 'actions', type: 'actions', width: 150,
            renderCell: (params) => (
                <ButtonGroup size="small">
                    <Button
                        style={{ width: 115 }}
                        component={Link}
                        to={`/company/create?id=${params.row.id}`}
                        color="secondary"
                        variant="outlined"
                        disabled={params.row.companyId}
                    >
                        <Localized id="register">
                            <span>Zarejestruj</span>
                        </Localized>
                    </Button>
                </ButtonGroup>
            )
        }
    ]

    if (isError) return <ErrorContainer error={error} />
    if (isLoading) return <LoadingContainer />

    return (
        <>
            <TableContainer component={Paper}>
                <DataGrid
                    localeText={getCustomLocaleText()}
                    style={{ minHeight: 100, height: 'calc(100vh - 200px)' }}
                    rows={filteredCompanies}
                    columns={columns}
                    initialState={{
                        sorting: { sortModel: [{ field: 'createDate', sort: 'desc' }] }
                    }}
                    slots={{ toolbar: CompaniesToolbar }}
                    slotProps={{
                        toolbar: {
                            onlyUnregistered,
                            onToggleOnlyUnregistered: setOnlyUnregistered
                        }
                    }}
                />
            </TableContainer>

            <FlexDiv>
                <Fab sx={{ marginTop: 2 }}
                    component={Link}
                    to="/company/create?companyToRegister=true"
                    color="secondary">
                    <AddIcon />
                </Fab>
            </FlexDiv>
        </>
    );
}
